import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useParams, useNavigate, useLocation } from "react-router-dom";
import ScreenHeader from '../../components/ScreenHeader';
import Display from '../../components/Display';
import CenteredTextContainer from '../../components/CenteredTextContainer';
import AcknowledgeForm from './AcknowledgeForm';
import useSWR from 'swr';
import { localAxios } from '../../api';

export function loader() {
  return "Acknowledge default data goes here, including first name, last name, email, and phone.";
}

function Acknowledge() {

  const { data, isLoading, isValidating } = useSWR('/api/me', localAxios);

  const RUBY_HILL = 1, MCCOY_COVE = 2, LONE_TREE = 3, GRANITE_CREEK = 4;
  const siteMap = new Map([
    ["Ruby Hill", RUBY_HILL],
    ["McCoy Cove", MCCOY_COVE],
    ["Lone Tree", LONE_TREE],
    ["Granite Creek", GRANITE_CREEK],
  ]);

  const [todayDate, setTodayDate] = useState(new Date());
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const confirmedMinors   = location.state?.confirmedMinors   ?? [];
  const unconfirmedMinors = location.state?.unconfirmedMinors ?? [];
  const [siteID, setSiteID] = useState(siteMap.get(params.siteName));

  const minorsListRef = useRef(null);
  const [atTop,       setAtTop]       = useState(true);
  const [atBottom,    setAtBottom]    = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [agreed,      setAgreed]      = useState(false);
  const [submitMsg,   setSubmitMsg]   = useState('');

  const canSubmit = hasScrolled && agreed;

  useEffect(() => {
    const el = minorsListRef.current;
    if (!el) return;
    const check = () => {
      setAtTop(el.scrollTop <= 1);
      setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
    };
    check();
    el.addEventListener('scroll', check);
    return () => el.removeEventListener('scroll', check);
  }, [confirmedMinors.length]);

  useEffect(() => {
    if (siteID) {
      localAxios.post('/api/workflow-state', { step: 'acknowledge', site_id: siteID })
        .catch((err) => console.log('Error saving workflow state:', err));
    }
  }, [siteID]);

  const postData = (event) => {
    event.preventDefault();

    const ackForm = event.target;
    setTodayDate(new Date());

    let formData = new FormData();
    formData.append("first_name", ackForm.first_name.value);
    formData.append("last_name", ackForm.last_name.value);
    formData.append("email", ackForm.email.value);
    formData.append("phone", ackForm.phone.value);
    formData.append("organization", ackForm.org.value);
    formData.append("acknowledge_flag", "1");
    formData.append("recorded_datetime", todayDate.toISOString());
    formData.append("siteID", siteID.toString());

    let formDataObj = Object.fromEntries(formData.entries());
    let formJSON = JSON.stringify(formDataObj);

    return localAxios.post('/api/acknowledge', formJSON).then(async response => {
      if (response) {
        if (confirmedMinors.length > 0 || unconfirmedMinors.length > 0) {
          try {
            await localAxios.post('/api/acknowledge/minors', {
              minors: confirmedMinors,
              unconfirmedMinors,
              siteID: siteID.toString(),
              recorded_datetime: new Date().toISOString(),
            });
          } catch (err) {
            console.log('Error submitting minor acknowledgments:', err);
          }
        }
        navigate("/success");
      }
    }).catch((error) => {
      if (error?.response?.status === 401) {
        navigate('/login');
      } else {
        alert('Something went wrong submitting your acknowledgment. Please try again.');
      }
    });
  };

  if (isLoading || isValidating) return <p>Loading...</p>;

  if (data?.data.loggedIn) {
    return (
      <div className="app-wide__container">
        <ScreenHeader message="Visitor Training" />

        <div className="app-wide__add-new acknowledge__site-info">
          <h2 className="acknowledge__site-name">{params.siteName}</h2>
          <p className="acknowledge__visit-count">
            This is visit <b>{data.data.visit_count + 1}</b>. After this visit, you will have{' '}
            <b>
              {5 - (data.data.visit_count + 1)}{' '}
              {(5 - (data.data.visit_count + 1)) > 1 ? 'more visits' : 'more visit'}
            </b> remaining.
          </p>
        </div>

        <div className="app-wide__main acknowledge__main">
          {confirmedMinors.length > 0 && (
            <div className="acknowledge__minors">
              <h3 className="acknowledge__minors-heading">Minors in your care</h3>
              <ul
                className="acknowledge__minors-list"
                ref={minorsListRef}
                style={{
                  maskImage: atTop && atBottom
                    ? 'none'
                    : atTop
                      ? 'linear-gradient(to bottom, black 70%, transparent 100%)'
                      : atBottom
                        ? 'linear-gradient(to bottom, transparent 0%, black 30%)'
                        : 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)',
                  WebkitMaskImage: atTop && atBottom
                    ? 'none'
                    : atTop
                      ? 'linear-gradient(to bottom, black 70%, transparent 100%)'
                      : atBottom
                        ? 'linear-gradient(to bottom, transparent 0%, black 30%)'
                        : 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)',
                }}
              >
                {confirmedMinors.map((m, i) => (
                  <li key={i} className="visit-submitted__person">
                    <span className="visit-submitted__person-name">{m.first_name} {m.last_name}</span>
                    {m.organization && (
                      <span className="visitor-tag visit-submitted__role-tag">{m.organization}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <form id="acknowledgeForm" onSubmit={postData}>
            <CenteredTextContainer onScrolledToBottom={() => setHasScrolled(true)} />
            <AcknowledgeForm />
            <Display message="I have read and do understand the above statement completely. I will follow the directions given." />
            <label className="acknowledge__agree-label">
              I Agree: <input
                type="checkbox"
                name="acknowledge_flag"
                checked={agreed}
                onChange={(e) => { setAgreed(e.target.checked); setSubmitMsg(''); }}
              />
            </label>
          </form>
        </div>

        <div className="app-wide__footer acknowledge__footer">
          {submitMsg && <p className="acknowledge__submit-hint">{submitMsg}</p>}
          <div
            onClick={() => {
              if (!canSubmit) {
                const parts = [];
                if (!hasScrolled) parts.push('scroll through the Acknowledgment');
                if (!agreed) parts.push('check "I Agree"');
                setSubmitMsg(`Please ${parts.join(' and ')} before submitting.`);
              }
            }}
          >
            <button
              type="submit"
              form="acknowledgeForm"
              className="acknowledge__submit-btn"
              disabled={!canSubmit}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    );
  } else {
    console.log('For some reason, the cookie is not detected.');
    return <Navigate to="/login" />;
  }
}

export default Acknowledge;
